import React from 'react';
import { useLocation } from 'react-router-dom';
import { Form, Row, Col, Modal, Button as BsButton } from 'react-bootstrap';
import { FaCogs, FaTimes, FaCheck } from 'react-icons/fa';
import { MdAddchart } from 'react-icons/md';
import GenericDropdown from './SelectDropdown';
import { useLabels } from '../hooks/useLabels';
import { concepts, periodicities, currencies, types } from '../utils';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button, Select, controlClass } from './EntityList';

const FORM_TYPE_SUBTITLES = {
	Deudas: 'Deudas',
	Pasividades: 'Pasividades',
	Alquileres: 'Alquileres',
	ActivosFijos: 'Activos fijos',
	VentasActivo: 'Ventas de activos',
	Cuentas: 'Cuentas',
	CuotasDeudas: 'Cuotas de deudas',
	CuotasCreditos: 'Cuotas de créditos',
	EstadoCuenta: 'Estado de cuenta',
	Cobros: 'Cobros',
};


const FilterForm = ({
	filterOptions,
	handleInputChange,
	clearFilter,
	handleApplyFilter,
	accountsList,
	isOpen,
	handleDropdownToggle,
	variant,
	reportTitle,
	submitDisabled = false,
	submitHelp,
	submitLabel = 'Generar reporte',
}) => {
	const isPanel = variant === 'panel';
	const { t } = useLabels();

	const handleSubmit = (e) => {
		e.preventDefault();
		handleApplyFilter(filterOptions.formType);
		// Close the "Filtro avanzado" modal once the filter is applied — only
		// relevant to the floating-modal variant, not the embedded reports panel
		// or the plain reports/predictions render path (no dropdown to close there).
		const isFilterModal = variant !== 'panel' && !pathname.includes('/reports') && !pathname.includes('/predictions');
		if (isFilterModal && handleDropdownToggle) {
			handleDropdownToggle();
		}
	};
	
	const debtConcepts = concepts;
	const creditConcepts = ['prestamos-amortizable', 'prestamos-interés'];
	const accountTypes = ['acreedores', 'deudores'];
	const paidOptions = ['Si', 'No'];
	const { pathname } = useLocation();

	// Configuration object for form fields by formType
	const formFieldsConfig = {
		'Deudas': {
			fields: ['concept', 'currency', 'dateRange', 'endDateRange', 'amountRange', 'capitalRange'],
			conceptItems: debtConcepts
		},
		'Pasividades': {
			fields: ['concept', 'periodicity', 'currency', 'dateRange', 'endDateRange', 'amountRange'],
			conceptItems: creditConcepts
		},
		'Alquileres': {
			fields: ['currency', 'periodInitRange', 'periodEndRange', 'amountRange']
		},
		'ActivosFijos': {
			fields: ['currency', 'priceRange']
		},
		'VentasActivo': {
			fields: ['currency', 'amountRange', 'dateRange']
		},
		'Cuentas': {
			fields: ['accountType', 'type', 'currency']
		},
		'CuotasDeudas': {
			fields: pathname.includes('/predictions') 
				? ['dateRangeWithMin'] 
				: ['concept', 'paid', 'currency', 'dateRange'],
			conceptItems: debtConcepts
		},
		'CuotasCreditos': {
			fields: pathname.includes('/predictions') 
				? ['dateRangeWithMin'] 
				: ['concept', 'paid', 'currency', 'dateRange'],
			conceptItems: creditConcepts
		},
		'EstadoCuenta': {
			fields: ['accountSelect', 'dateRange']
		},
		'Cobros': {
			fields: ['concept', 'currency', 'dateRange'],
			conceptItems: ['all', ...debtConcepts]
		},
	};

	// Shared ".field" markup for the advanced-filter modal (label + control) —
	// used by every formType's field renderers below.
	const fieldLabelClass = isPanel
		? 'tw:mb-1.5 tw:block tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3'
		: 'tw:mb-1.5 tw:block tw:text-[12.5px] tw:font-semibold tw:text-ink-2';
	const rangeInputClass = `${controlClass} tw:w-full tw:min-w-0 tw:px-[11px]`;
	const rangeGridClass = isPanel ? 'tw:grid tw:grid-cols-2 tw:gap-2' : 'tw:grid tw:grid-cols-2 tw:gap-3';

	const toSelectOptions = (items) => {
		const normalized = (items || []).map((item) => (
			typeof item === 'object' && item !== null
				? item
				: { value: item, label: item }
		));
		return [{ value: 'all', label: 'Todas' }, ...normalized.filter((item) => item.value !== 'all')];
	};

	// Reusable form field components
	const renderDropdownField = (label, name, items) => (
		<div className='tw:flex tw:flex-col tw:gap-1.5' key={name}>
			<label className={fieldLabelClass}>{label}</label>
			<Select
				name={name}
				options={toSelectOptions(items)}
				value={filterOptions[name]}
				onChange={handleInputChange}
				className='tw:w-full'
			/>
		</div>
	);

	const renderDateRange = (label, fromName, toName, minDate = null) => (
		<div className='tw:flex tw:flex-col tw:gap-1.5' key={fromName}>
			<label className={fieldLabelClass}>{label}</label>
			<div className={rangeGridClass}>
				<input
					type='date'
					name={fromName}
					value={filterOptions[fromName]}
					onChange={handleInputChange}
					min={minDate}
					className={rangeInputClass}
				/>
				<input
					type='date'
					name={toName}
					value={filterOptions[toName]}
					onChange={handleInputChange}
					min={minDate}
					className={rangeInputClass}
				/>
			</div>
		</div>
	);

	const renderNumberRange = (label, fromName, toName, placeholder = 'Desde') => (
		<div className='tw:flex tw:flex-col tw:gap-1.5' key={fromName}>
			<label className={fieldLabelClass}>{label}</label>
			<div className={rangeGridClass}>
				<input
					type='number'
					name={fromName}
					value={filterOptions[fromName]}
					onChange={handleInputChange}
					placeholder={placeholder}
					className={rangeInputClass}
				/>
				<input
					type='number'
					name={toName}
					value={filterOptions[toName]}
					onChange={handleInputChange}
					placeholder='Hasta'
					className={rangeInputClass}
				/>
			</div>
		</div>
	);

	const renderVentasActivoFields = () => (
		<>
			{renderDropdownField('Moneda', 'currency', currencies)}
			{renderNumberRange('Monto de venta', 'amountFrom', 'amountTo')}
			{renderDateRange('Fecha', 'dateFrom', 'dateTo')}
		</>
	);

	const renderFormFields = (config) => {
		if (!config) return null;

		if (filterOptions.formType === 'VentasActivo') {
			return renderVentasActivoFields();
		}

		return config.fields.map(fieldType => {
			switch (fieldType) {
			case 'concept':
				return renderDropdownField('Concepto', 'concept', config.conceptItems, 9);
			case 'periodicity':
				return renderDropdownField('Periodicidad', 'periodicity', periodicities, 9);
			case 'currency':
				return renderDropdownField('Moneda', 'currency', currencies, 9);
			case 'paid':
				return renderDropdownField('Pago', 'paid', paidOptions, 9);
			case 'accountType':
				return renderDropdownField(`${t('entity.account.debtor')} o ${t('entity.account.creditor')}`, 'accountType', accountTypes, 9);
			case 'type':
				return renderDropdownField('Tipo', 'type', types, 9);
			case 'accountSelect':
				return renderDropdownField(t('entity.account'), 'accountId', accountsList || [], 9);
			case 'dateRange':
				return renderDateRange('Fecha', 'dateFrom', 'dateTo');
			case 'dateRangeWithMin':
				return renderDateRange('Fecha vencimiento', 'dateFrom', 'dateTo', moment.utc().startOf('month').format('YYYY-MM-DD'));
			case 'endDateRange':
				return renderDateRange('Vto', 'endDateFrom', 'endDateTo');
			case 'periodInitRange':
				return renderDateRange('Fecha', 'periodInitFrom', 'periodInitTo');
			case 'periodEndRange':
				return renderDateRange('Vto', 'periodEndFrom', 'periodEndTo');
			case 'amountRange':
				return renderNumberRange('Monto', 'amountFrom', 'amountTo');
			case 'capitalRange':
				return renderNumberRange('Capital', 'capitalFrom', 'capitalTo');
			case 'priceRange':
				return renderNumberRange('Monto', 'priceFrom', 'priceTo');
			default:
				return null;
			}
		});
	};

	const currentFormConfig = formFieldsConfig[filterOptions.formType];
	const isModalView = !pathname.includes('/reports') && !pathname.includes('/predictions');
	const modalSubtitle = FORM_TYPE_SUBTITLES[filterOptions.formType] || filterOptions.formType;

	const bodyContent = (
		<>
			{!currentFormConfig && (
				<>
					{filterOptions.formType === 'Deudas' && (
						<>
							<Row>
								<Col xs={12} md={6}>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3}>
										Concepto:
										</Form.Label>
										<Col md={9}>
											<GenericDropdown
												label='Concepto'
												items={debtConcepts}
												selected={filterOptions.concept}
												onChange={handleInputChange}
												name='concept'
												isInFilterForm={true}
											/>
										</Col>
									</Form.Group>
								</Col>
								<Col xs={12} md={6}>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3} size='sm'>
										Moneda:
										</Form.Label>
										<Col md={9}>
											<GenericDropdown
												label='Moneda'
												items={currencies}
												selected={filterOptions.currency}
												onChange={handleInputChange}
												name='currency'
												isInFilterForm={true}
											/>
										</Col>
									</Form.Group>
								</Col>
							</Row>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Vto:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.endDateFrom}
										onChange={(date) => handleInputChange(date, 'endDateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.endDateTo}
										onChange={(date) => handleInputChange(date, 'endDateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
							<Row>
								<Col xs={12} md={6}>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={2} size='sm'>
										Monto:
										</Form.Label>
										<Col md={5}>
											<Form.Control
												type='number'
												name='amountFrom'
												size='sm'
												value={filterOptions.amountFrom}
												onChange={handleInputChange}
												placeholder='Desde'
											/>
										</Col>
										<Col md={5}>
											<Form.Control
												type='number'
												name='amountTo'
												size='sm'
												value={filterOptions.amountTo}
												onChange={handleInputChange}
												placeholder='Hasta'
											/>
										</Col>
									</Form.Group>
								</Col>
								<Col xs={12} md={6}>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={2} size='sm'>
										Capital:
										</Form.Label>
										<Col md={5}>
											<Form.Control
												type='number'
												name='capitalFrom'
												size='sm'
												value={filterOptions.capitalFrom}
												onChange={handleInputChange}
												placeholder='Desde'
											/>
										</Col>
										<Col md={5}>
											<Form.Control
												type='number'
												name='capitalTo'
												size='sm'
												value={filterOptions.capitalTo}
												onChange={handleInputChange}
												placeholder='Hasta'
											/>
										</Col>
									</Form.Group>
								</Col>
							</Row>
						</>
					)}
					{filterOptions.formType === 'Pasividades' && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3}>
								Concepto:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Concepto'
										items={creditConcepts}
										selected={filterOptions.concept}
										onChange={handleInputChange}
										name='concept'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3}>
								Periodicidad:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Periodicidad'
										items={periodicities}
										selected={filterOptions.periodicity}
										onChange={handleInputChange}
										name='periodicity'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Vto:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.endDateFrom}
										onChange={(date) => handleInputChange(date, 'endDateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.endDateTo}
										onChange={(date) => handleInputChange(date, 'endDateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Monto:
								</Form.Label>
								<Col md={5}>
									<Form.Control
										type='number'
										name='amountFrom'
										size='sm'
										value={filterOptions.amountFrom}
										onChange={handleInputChange}
										placeholder='Desde'
									/>
								</Col>
								<Col md={5}>
									<Form.Control
										type='number'
										name='amountTo'
										size='sm'
										value={filterOptions.amountTo}
										onChange={handleInputChange}
										placeholder='Hasta'
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'Alquileres' && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.periodInitFrom}
										onChange={(date) => handleInputChange(date, 'periodInitFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.periodInitTo}
										onChange={(date) => handleInputChange(date, 'periodInitTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Vto:
								</Form.Label>
								<Col md={5}>
									<Form.Control
										type='date'
										name='periodEndFrom'
										size='sm'
										value={filterOptions.periodEndFrom}
										onChange={handleInputChange}
									/>
								</Col>
								<Col md={5}>
									<Form.Control
										type='date'
										name='periodEndTo'
										size='sm'
										value={filterOptions.periodEndTo}
										onChange={handleInputChange}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Monto:
								</Form.Label>
								<Col md={5}>
									<Form.Control
										type='number'
										name='amountFrom'
										size='sm'
										value={filterOptions.amountFrom}
										onChange={handleInputChange}
										placeholder='Desde'
									/>
								</Col>
								<Col md={5}>
									<Form.Control
										type='number'
										name='amountTo'
										size='sm'
										value={filterOptions.amountTo}
										onChange={handleInputChange}
										placeholder='Hasta'
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'ActivosFijos' && (
						<>
							{pathname.includes('/reports') ? (
								<>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3} size='sm'>
										Nombre de activo:
										</Form.Label>
										<Col md={9}>
											<Form.Control
												type='text'
												name='name'
												size='sm'
												value={filterOptions.name}
												onChange={handleInputChange}
												placeholder='Nombre de activo'
											/>
										</Col>
									</Form.Group>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3} size='sm'>
											{t('entity.assetOwner')}:
										</Form.Label>
										<Col md={9}>
											<GenericDropdown
												label='owner'
												items={accountsList ? accountsList : ''}
												selected={filterOptions.owner}
												onChange={handleInputChange}
												name='owner'
												isInFilterForm={true}
												accountsList={true}
											/>
										</Col>
									</Form.Group>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3}>
										Vendido?:
										</Form.Label>
										<Col md={9}>
											<GenericDropdown
												label='Vendido'
												items={paidOptions}
												selected={filterOptions.sold}
												onChange={handleInputChange}
												name='sold'
												isInFilterForm={true}
											/>
										</Col>
									</Form.Group>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3} size='sm'>
										Padron:
										</Form.Label>
										<Col md={9}>
											<Form.Control
												type='text'
												name='lot'
												size='sm'
												value={filterOptions.lot}
												onChange={handleInputChange}
												placeholder='Padron'
											/>
										</Col>
									</Form.Group>
									<Form.Group as={Row} className='mb-3 align-items-center'>
										<Form.Label column md={3} size='sm'>
										Ubicación:
										</Form.Label>
										<Col md={9}>
											<Form.Control
												type='text'
												name='ubication'
												size='sm'
												value={filterOptions.ubication}
												onChange={handleInputChange}
												placeholder='Ubicacion'
											/>
										</Col>
									</Form.Group>
								</>
							) : null}
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Monto:
								</Form.Label>
								<Col md={5}>
									<Form.Control
										type='number'
										name='priceFrom'
										size='sm'
										value={filterOptions.priceFrom}
										onChange={handleInputChange}
										placeholder='Desde'
									/>
								</Col>
								<Col md={5}>
									<Form.Control
										type='number'
										name='priceTo'
										size='sm'
										value={filterOptions.priceTo}
										onChange={handleInputChange}
										placeholder='Hasta'
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'VentasActivo' && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Monto:
								</Form.Label>
								<Col md={5}>
									<Form.Control
										type='number'
										name='amountFrom'
										size='sm'
										value={filterOptions.amountFrom}
										onChange={handleInputChange}
										placeholder='Desde'
									/>
								</Col>
								<Col md={5}>
									<Form.Control
										type='number'
										name='amountTo'
										size='sm'
										value={filterOptions.amountTo}
										onChange={handleInputChange}
										placeholder='Hasta'
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'Cuentas' && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
									{`${t('entity.account.debtor')} o ${t('entity.account.creditor')}`}:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='TipoCuenta'
										items={accountTypes}
										selected={filterOptions.accountType}
										onChange={handleInputChange}
										name='accountType'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Tipo:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Tipo'
										items={types}
										selected={filterOptions.type}
										onChange={handleInputChange}
										name='type'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'CuotasDeudas' && !pathname.includes('/predictions') && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3}>
								Concepto:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Concepto'
										items={debtConcepts}
										selected={filterOptions.concept}
										onChange={handleInputChange}
										name='concept'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3}>
								Pago:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Pago'
										items={paidOptions}
										selected={filterOptions.paid}
										onChange={handleInputChange}
										name='paid'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha vencimiento:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha de ultimo cobro:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.lastPaymentFrom}
										onChange={(date) => handleInputChange(date, 'lastPaymentFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.lastPaymentTo}
										onChange={(date) => handleInputChange(date, 'lastPaymentTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'CuotasDeudas' && pathname.includes('/predictions') && (
						<>
							{!pathname.includes('/reports') && !pathname.includes('/predictions') ? (
								<h4 className='text-center p-2'>
									<FaCogs></FaCogs> Filtro avanzado ({filterOptions.formType})
								</h4>
							) : (
								''
							)}
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha vencimiento:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
										minDate={moment.utc().startOf('month').toDate()}
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
										minDate={moment.utc().startOf('month').toDate()}
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'CuotasCreditos' && pathname.includes('/predictions') && (
						<>
							{!pathname.includes('/reports') && !pathname.includes('/predictions') ? (
								<h4 className='text-center p-2'>
									<FaCogs></FaCogs> Filtro avanzado ({filterOptions.formType})
								</h4>
							) : (
								''
							)}
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha vencimiento:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
										minDate={moment.utc().startOf('month').toDate()}
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
										minDate={moment.utc().startOf('month').toDate()}
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'CuotasCreditos' && !pathname.includes('/predictions') && (
						<>
							{!pathname.includes('/reports') && !pathname.includes('/predictions') ? (
								<h4 className='text-center p-2'>
									<FaCogs></FaCogs> Filtro avanzado ({filterOptions.formType})
								</h4>
							) : (
								''
							)}
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3}>
								Concepto:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Concepto'
										items={creditConcepts}
										selected={filterOptions.concept}
										onChange={handleInputChange}
										name='concept'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3}>
								Pago:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Pago'
										items={paidOptions}
										selected={filterOptions.paid}
										onChange={handleInputChange}
										name='paid'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha vencimiento:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
									/>
								</Col>
							</Form.Group>
						</>
					)}
					{filterOptions.formType === 'EstadoCuenta' && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
									{t('entity.account')}:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Cuenta'
										items={accountsList ? accountsList : ''}
										selected={filterOptions.accountId}
										onChange={handleInputChange}
										name='accountId'
										isInFilterForm={true}
										accountsList={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={2} size='sm'>
								Fecha vencimiento:
								</Form.Label>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateFrom}
										onChange={(date) => handleInputChange(date, 'dateFrom')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
										minDate={moment.utc().startOf('month').toDate()}
									/>
								</Col>
								<Col md={5}>
									<DatePicker
										selected={filterOptions.dateTo}
										onChange={(date) => handleInputChange(date, 'dateTo')}
										placeholderText='dd/mm/yyyy'
										dateFormat='dd/MM/yyyy'
										className='form-control form-control-sm'
										minDate={moment.utc().startOf('month').toDate()}
									/>
								</Col>
							</Form.Group>
						</>
					)}

					{!pathname.includes('/reports') && !pathname.includes('/predictions') && renderFormFields(currentFormConfig)}

					{!pathname.includes('/reports') && !pathname.includes('/predictions') ? (
						<Row className='justify-content-around mt-2 mb-2'>
							<button className='w-fit-content header-button' onClick={clearFilter}>
							Limpiar Filtro
							</button>
							<button className='w-fit-content header-button' type='submit'>Aplicar Filtro</button>
						</Row>
					) : (
						<Row className='d-flex justify-content-end align-items-center mt-3'>
							<Col className='d-flex justify-content-end col-10'>
								<BsButton variant='primary' size='lg' type='submit'>
								Generar Reporte
								</BsButton>
							</Col>
							<Col className='d-flex justify-content-start col-2'>
								<BsButton variant='secondary' size='sm' className='mr-2' onClick={clearFilter}>
								Limpiar Filtro
								</BsButton>
							</Col>
						</Row>
					)}
					{filterOptions.formType === 'Cobros' && (
						<>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Concepto:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Concepto'
										items={concepts}
										selected={filterOptions.concept}
										onChange={handleInputChange}
										name='concept'
										isInFilterForm={true}
									/>
								</Col>
							</Form.Group>
							<Form.Group as={Row} className='mb-3 align-items-center'>
								<Form.Label column md={3} size='sm'>
								Moneda:
								</Form.Label>
								<Col md={9}>
									<GenericDropdown
										label='Moneda'
										items={currencies}
										selected={filterOptions.currency}
										onChange={handleInputChange}
										name='currency'
										isInFilterForm={true}
									/>
								</Col>
								<Form.Group as={Row} className='mb-3 align-items-center'>
									<Form.Label column md={2} size='sm'>
									Fecha:
									</Form.Label>
									<Col md={5}>
										<DatePicker
											selected={filterOptions.dateFrom}
											onChange={(date) => handleInputChange(date, 'dateFrom')}
											placeholderText='dd/mm/yyyy'
											dateFormat='dd/MM/yyyy'
											className='form-control form-control-sm'
											minDate={moment.utc().startOf('month').toDate()}
										/>
									</Col>
									<Col md={5}>
										<DatePicker
											selected={filterOptions.dateTo}
											onChange={(date) => handleInputChange(date, 'dateTo')}
											placeholderText='dd/mm/yyyy'
											dateFormat='dd/MM/yyyy'
											className='form-control form-control-sm'
											minDate={moment.utc().startOf('month').toDate()}
										/>
									</Col>
								</Form.Group>
							</Form.Group>
						</>
					)}
				</>
			)}
			{currentFormConfig && renderFormFields(currentFormConfig)}
		</>
	);

	const footerContent = (
		<>
			{!pathname.includes('/reports') && !pathname.includes('/predictions') ? (
				<>
					<Button onClick={clearFilter}>Limpiar filtro</Button>
					<Button variant='primary' type='submit' icon={<FaCheck size={14} />}>Aplicar filtro</Button>
				</>
			) : (
				<Row className='d-flex justify-content-end align-items-center mt-3'>
					<Col className='d-flex justify-content-end col-10'>
						<BsButton variant='primary' size='lg' type='submit'>
								Generar Reporte
						</BsButton>
					</Col>
					<Col className='d-flex justify-content-start col-2'>
						<BsButton variant='secondary' size='sm' className='mr-2' onClick={clearFilter}>
								Limpiar Filtro
						</BsButton>
					</Col>
				</Row>
			)}
		</>
	);

	if (isPanel) {
		return (
			<Form onSubmit={handleSubmit}>
				<div className='tw:flex tw:flex-col tw:gap-[18px] tw:px-5 tw:py-[18px]'>{bodyContent}</div>
				<div className='tw:flex tw:flex-col tw:gap-2.5 tw:border-t tw:border-border tw:px-5 tw:py-3.5'>
					{reportTitle && (
						<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-3'>
							Se generará &quot;{reportTitle}&quot; en formato XLSX.
						</div>
					)}
					{submitHelp && (
						<div className='tw:rounded-[7px] tw:bg-accent-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:leading-[1.4] tw:text-accent-ink'>
							{submitHelp}
						</div>
					)}
					<div className='tw:flex tw:items-center tw:gap-2'>
						<Button variant='ghost' onClick={clearFilter}>Limpiar</Button>
						<Button variant='primary' type='submit' icon={<MdAddchart size={15} />} className='tw:flex-1 tw:justify-center disabled:tw:cursor-not-allowed disabled:tw:opacity-50' disabled={submitDisabled}>
							{submitLabel}
						</Button>
					</div>
				</div>
			</Form>
		);
	}

	if (isModalView) {
		return (
			<Modal
				show={isOpen}
				onHide={handleDropdownToggle}
				centered
				contentClassName='tw:rounded-[14px] tw:shadow-overlay tw:border-0 tw:overflow-hidden tw:font-sans'
				dialogClassName='tw:max-w-[560px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3 tw:border-b tw:border-border tw:px-[22px] tw:pt-5 tw:pb-3.5'>
					<div className='tw:flex-1'>
						<h2 className='tw:m-0 tw:text-[17px] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>Filtro avanzado</h2>
						<p className='tw:mt-1 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>{modalSubtitle}</p>
					</div>
					<button
						type='button'
						onClick={handleDropdownToggle}
						className='tw:h-[26px] tw:w-[26px] tw:grid tw:place-items-center tw:rounded-[7px] tw:border-0 tw:bg-transparent tw:text-ink-2 tw:hover:bg-surface-2'
						aria-label='Cerrar'
					>
						<FaTimes size={18} />
					</button>
				</div>
				<div className='tw:max-h-[70vh] tw:overflow-auto'>
					<Form onSubmit={handleSubmit}>
						<div className='tw:flex tw:flex-col tw:gap-5 tw:px-[22px] tw:py-[18px]'>
							{bodyContent}
						</div>
						<div className='tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-3.5'>
							{footerContent}
						</div>
					</Form>
				</div>
			</Modal>
		);
	}

	return (
		<div>
			<Form onSubmit={handleSubmit}>
				{bodyContent}
				{footerContent}
			</Form>
		</div>
	);
};

export default FilterForm;
